import {
  TYPE_RESPONSE_INVOICING_QUANTITY,
  TYPE_RESPONSE_PAYMENT_MODE,
  TYPE_RESPONSE_PAYMENT_CURRENCY,
  TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM,
  TYPE_RESPONSE_LAW,
  TYPE_RESPONSE_ARBITRATION,
  TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION,
  TYPE_RESPONSE_ADDITIONAL_NOTES,
  TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM_AGREED,
  TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION_AGREED,
  TYPE_RESPONSE_ADDITIONAL_NOTES_AGREED,
  TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM,
  TYPE_RESPONSE_DELAY_PENALTY_AMOUNT,
  TYPE_RESPONSE_DESCRIPTION_OF_DELAY_PENALTY_TERM,
  TYPE_RESPONSE_DESCRIPTION_OF_DELAY_PENALTY_TERM_AGREED,
} from '../respond/helpers';
import { isSameHtml, isSameId } from './helpers';
import { formatNumber } from '../../../utils/format';

// Delay Penalty Terms: amount = `kind: 'number'` (thread 22 = pola kelompok
// 5, agree 23); description = free-text (thread 24, agree 25).
export const DELAY_PENALTY_TERM_FIELDS = [
  {
    key: 'delayPenaltyAmount',
    typeResponse: TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM,
    agreeTypeResponse: TYPE_RESPONSE_DELAY_PENALTY_AMOUNT,
    label: 'Delay Penalty Amount',
    field: 'delayPenaltyAmount',
    kind: 'number',
    unit: 'USD',
  },
  {
    key: 'descriptionOfDelayPenaltyTerms',
    typeResponse: TYPE_RESPONSE_DESCRIPTION_OF_DELAY_PENALTY_TERM,
    agreeTypeResponse: TYPE_RESPONSE_DESCRIPTION_OF_DELAY_PENALTY_TERM_AGREED,
    label: 'Description of Delay Penalty Terms',
    field: 'descriptionOfDelayPenaltyTerms',
  },
];

// Satu thread respond + counter + Agree & Lock per field Payment Terms /
// Compliance. `field` = kolom di procurement & row counter; `entity` = objek
// master (choice). Tanpa entity = freetext (HTML). `agreeTypeResponse` =
// baris agreement (PUT agree-field): choice = type sendiri, free-text 19-21.
export const PAYMENT_TERM_FIELDS = [
  {
    key: 'invoicingQuantity',
    typeResponse: TYPE_RESPONSE_INVOICING_QUANTITY,
    agreeTypeResponse: TYPE_RESPONSE_INVOICING_QUANTITY,
    label: 'Invoicing Quantity',
    field: 'invoicingQuantityId',
    entity: 'invoicingQuantity',
  },
  {
    key: 'paymentMode',
    typeResponse: TYPE_RESPONSE_PAYMENT_MODE,
    agreeTypeResponse: TYPE_RESPONSE_PAYMENT_MODE,
    label: 'Mode',
    field: 'modeInvoicingQuantityId',
    entity: 'modeInvoicingQuantity',
  },
  {
    key: 'paymentCurrency',
    typeResponse: TYPE_RESPONSE_PAYMENT_CURRENCY,
    agreeTypeResponse: TYPE_RESPONSE_PAYMENT_CURRENCY,
    label: 'Currency',
    field: 'paymentCurrencyId',
    entity: 'paymentCurrency',
  },
  {
    key: 'descriptionOfPaymentTerm',
    typeResponse: TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM,
    agreeTypeResponse: TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM_AGREED,
    label: 'Description of Payment Term',
    field: 'descriptionOfPaymentTerms',
  },
];

export const COMPLIANCE_FIELDS = [
  {
    key: 'law',
    typeResponse: TYPE_RESPONSE_LAW,
    agreeTypeResponse: TYPE_RESPONSE_LAW,
    label: 'Law',
    field: 'lawId',
    entity: 'law',
  },
  {
    key: 'arbitration',
    typeResponse: TYPE_RESPONSE_ARBITRATION,
    agreeTypeResponse: TYPE_RESPONSE_ARBITRATION,
    label: 'Arbitration',
    field: 'arbitrationId',
    entity: 'arbitration',
  },
  {
    key: 'otherTermsAndCondition',
    typeResponse: TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION,
    agreeTypeResponse: TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION_AGREED,
    label: 'Other Terms & Condition',
    field: 'otherTermsAndCondition',
  },
  {
    key: 'additionalNotes',
    typeResponse: TYPE_RESPONSE_ADDITIONAL_NOTES,
    agreeTypeResponse: TYPE_RESPONSE_ADDITIONAL_NOTES_AGREED,
    label: 'Additional Notes',
    field: 'additionalNotes',
  },
];

export const PAYMENT_COMPLIANCE_FIELDS = [
  ...DELAY_PENALTY_TERM_FIELDS,
  ...PAYMENT_TERM_FIELDS,
  ...COMPLIANCE_FIELDS,
];

export const isNumberField = def => def?.kind === 'number';

export const isFreeTextField = def => !def?.entity && !isNumberField(def);

// '' / null → null; selain itu angka (5 dan '5.0' sama). Bukan angka → NaN.
const toNumberOrNull = value =>
  value == null || String(value).trim() === '' ? null : Number(value);

export const isSameNumber = (left, right) =>
  toNumberOrNull(left) === toNumberOrNull(right);

// Display nilai field: number → "1,500 USD", lainnya apa adanya.
export const formatCounterFieldValue = (def, value) => {
  if (!isNumberField(def)) return value;
  const text = formatNumber(value);
  return text === '-' || !def.unit ? text : `${text} ${def.unit}`;
};

// Delay Penalty: buyer tidak isi amount; Mitra Confirm (tarif invoice
// standar) atau Counter (amount USD > 0).
export const DELAY_PENALTY_SUPPLIER_ENTRY_NOTE =
  'Delay penalty amount shall be entered by the registered supplier if the invoice percentage rate is not applied.';
export const DELAY_PENALTY_COUNTER_HINT =
  'Enter the maximum capped penalty amount in USD if you do not wish to use the standard invoice percentage rate (e.g., Capped at maximum USD 10,000 PDPR).';
export const DELAY_PENALTY_CONFIRMED_LABEL =
  'Confirmed — standard invoice percentage rate';
export const DELAY_PENALTY_UNRESOLVED_MESSAGE =
  'Please confirm or counter the Delay Penalty Terms before submitting the offer.';

export const isPositiveAmount = value => toNumberOrNull(value) > 0;

export const isDelayPenaltyResolved = ({ isConfirmed, counter } = {}) =>
  isConfirmed === true || isPositiveAmount(counter?.delayPenaltyAmount);

const formatDelayPenaltyAmount = value =>
  formatCounterFieldValue(DELAY_PENALTY_TERM_FIELDS[0], value);

// Display amount buyer (read-only): kosong → catatan supplier.
export const formatBuyerDelayPenaltyAmount = value =>
  toNumberOrNull(value) == null
    ? DELAY_PENALTY_SUPPLIER_ENTRY_NOTE
    : formatDelayPenaltyAmount(value);

// Urutan: counter Mitra (> 0) > confirm > amount buyer legacy > '-'.
// Final agreed (type 23) dicek caller lebih dulu.
export const resolveDelayPenaltyAmountText = ({
  counterAmount,
  isConfirmed,
  buyerAmount,
} = {}) => {
  if (isPositiveAmount(counterAmount)) {
    return formatDelayPenaltyAmount(counterAmount);
  }
  if (isConfirmed) return DELAY_PENALTY_CONFIRMED_LABEL;
  return formatDelayPenaltyAmount(buyerAmount);
};

// Nilai field dari procurement / row counter (choice → id, freetext → HTML,
// number → angka mentah).
export const getCounterFieldValue = (source, def) => {
  if (!source || !def) return '';
  if (isNumberField(def) || isFreeTextField(def)) {
    return source[def.field] ?? '';
  }
  const entity = source[def.entity];
  return source[def.field] || entity?.id || entity?.Id || '';
};

const toNullable = value =>
  value == null || String(value).trim() === '' ? null : value;

// Row counter lengkap: basis = counter terakhir Mitra (atau original
// procurement), hanya `changedDef` yang diganti — field lain tetap.
export const buildFieldCounterRow = (
  defs,
  latestCounter,
  procurement,
  changedDef,
  value
) =>
  defs.reduce((row, def) => {
    const next =
      def.field === changedDef?.field
        ? value
        : getCounterFieldValue(latestCounter || procurement, def);
    row[def.field] = isNumberField(def)
      ? toNumberOrNull(next)
      : toNullable(next);
    return row;
  }, {});

// Field pilihan (Invoicing/Mode/Currency, Law/Arbitration): satu thread
// kelompok (type 5/6) + Agree & Lock per field (baris agreement type 8-13).
// Field free-text tetap satu thread per field.
export const isGroupAgreeField = def => !isFreeTextField(def);

// Bisa di-agree hanya jika counter terakhir Mitra beda dari original
// (choice = id, free-text = HTML dinormalisasi).
export const isFieldAgreeable = (def, latestCounter, procurement) => {
  if (!def || !latestCounter) return false;
  if (isNumberField(def)) {
    return !isSameNumber(latestCounter[def.field], procurement?.[def.field]);
  }
  if (isFreeTextField(def)) {
    return !isSameHtml(latestCounter[def.field], procurement?.[def.field]);
  }
  return !isSameId(
    latestCounter[def.field],
    procurement?.[def.field],
    latestCounter[def.entity],
    procurement?.[def.entity]
  );
};

export const isFieldCountered = isFieldAgreeable;

export const hasAnyFieldCountered = (defs, latestCounter, procurement) =>
  (defs ?? []).some(def => isFieldCountered(def, latestCounter, procurement));

const findAgreedRow = rows =>
  (Array.isArray(rows) ? rows : []).find(
    item => item?.isLocked === true && String(item?.finalValue ?? '').trim()
  );

// Free-text (19-21 / lock thread lama): Final = HTML apa adanya.
export const getAgreedFinalHtml = rows =>
  findAgreedRow(rows)?.finalValue ?? null;

// Final free-text: agreement 19-21, fallback lock lama di thread 11/14/15.
export const getFreeTextAgreedFinal = (def, responsesByType) =>
  getAgreedFinalHtml(responsesByType?.[def?.agreeTypeResponse]) ??
  getAgreedFinalHtml(responsesByType?.[def?.typeResponse]);

// Baris agreement aktif → label Final (BE HTML-encode → decode ke teks).
export const getAgreedFinal = rows => {
  const row = findAgreedRow(rows);
  if (!row) return null;
  const doc = new DOMParser().parseFromString(
    String(row.finalValue),
    'text/html'
  );
  return (doc.documentElement.textContent || '').trim() || null;
};

// Thread kelompok tanpa Agree level thread: selesai bila ada field yang
// dicounter dan semuanya sudah di-agree. items = [{ agreed, agreeable }].
export const isCounterGroupAgreed = items => {
  const relevant = (items ?? []).filter(item => item.agreed || item.agreeable);
  return relevant.length > 0 && relevant.every(item => item.agreed);
};
